import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { SortDescriptor, orderBy, process } from '@progress/kendo-data-query'
import MD5 from 'crypto-js/md5'
import { v4 as makeUuid } from 'uuid'
import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { RecipientPolicy, RecipientPolicyStatus } from 'types/Settings'
import {
  RecipientPoliciesForm,
  RecipientPoliciesFormState,
  useRecipientPoliciesForm
} from 'components/pages/inboundSettings/recipientPolicies/useRecipientPoliciesForm'

export interface UseRecipientPoliciesTableLogicProps {
  data: RecipientPolicy[]
  onAddItem: (ip: RecipientPolicy) => void
  onRemoveItem: (id: string) => void
  delegateDirtyFormState: Dispatch<SetStateAction<boolean>>
}

export interface State {
  idToRemove: string
  sort: SortDescriptor[]
  form: RecipientPoliciesForm
  formState: RecipientPoliciesFormState
  error: string
  tableData: {
    data: RecipientPolicy[]
    total: number
  }
}

export interface EventHandlers {
  onAdd: () => void
  onRemove: (id: string) => void
  onConfirmRemove: () => void
  onConfirmCancel: () => void
  handleOnInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleSortChange: (e: GridSortChangeEvent) => void
  onPolicyChange: (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => void
}

export type UseRecipientPoliciesTableLogic = [State, EventHandlers]

const initialSort = [{ field: 'name', dir: 'asc' } as SortDescriptor]

export const enum POLICIES_INPUT_NAMES {
  NAME = 'name',
  POLICY = 'policy',
  COMMENT = 'comment'
}

const INITIAL_FORM_STATE = { name: '', policy: RecipientPolicyStatus.scan, comment: '' }

const BLANK_ENTRY = { ...INITIAL_FORM_STATE, id: '' }

export const useRecipientPoliciesTableLogic = ({
  data,
  onAddItem,
  onRemoveItem,
  delegateDirtyFormState
}: UseRecipientPoliciesTableLogicProps): UseRecipientPoliciesTableLogic => {
  const form = useRecipientPoliciesForm({
    initialState: INITIAL_FORM_STATE,
    delegateIsDirtyForm: delegateDirtyFormState
  })
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort)
  const [idToRemove, setIdToRemove] = useState('')

  const onAdd = useCallback(() => {
    const newPolicy: RecipientPolicy = {
      ...form.getState(),
      id: MD5(makeUuid()).toString()
    }
    if (form.validate(newPolicy, { data })) {
      onAddItem(newPolicy)
    }
  }, [form, data, onAddItem])

  const onRemove = useCallback(
    (id: string) => {
      setIdToRemove(id)
    },
    [setIdToRemove]
  )

  const onConfirmRemove = useCallback(() => {
    onRemoveItem(idToRemove)
    setIdToRemove('')
  }, [idToRemove, onRemoveItem, setIdToRemove])

  const onConfirmCancel = useCallback(() => {
    setIdToRemove('')
  }, [setIdToRemove])

  const tableData = useMemo(() => {
    const orderedData = orderBy(data, sort)
    const { data: processedData } = process(orderedData, {})

    // Have the first item as an edit item.  This method works with sorting.
    processedData.unshift(BLANK_ENTRY)
    return {
      data: processedData as RecipientPolicy[],
      total: processedData.length || 0
    }
  }, [data, sort])

  const handleSortChange = useCallback((e: GridSortChangeEvent) => {
    setSort(e.sort)
  }, [])

  const handleOnInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      // eslint-disable-next-line default-case
      switch (true) {
        case name === POLICIES_INPUT_NAMES.NAME:
          form.fields.name.setValue(value)
          break
        case name === POLICIES_INPUT_NAMES.COMMENT:
          form.fields.comment.setValue(value)
          break
      }
      form.delegateIsDirty()
    },
    [form]
  )

  const onPolicyChange = useCallback(
    (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      const { value } = e.target
      if (typeof value === 'string') {
        form.fields.policy.setValue(value)
        form.delegateIsDirty()
      }
    },
    [form]
  )

  return useMemo(
    () => [
      {
        idToRemove,
        sort,
        form,
        formState: form.getState(),
        error: form.error,
        tableData
      },
      {
        onAdd,
        onRemove,
        onConfirmRemove,
        onConfirmCancel,
        handleOnInputChange,
        handleSortChange,
        onPolicyChange
      }
    ],
    [
      idToRemove,
      sort,
      form,
      tableData,
      onAdd,
      onRemove,
      onConfirmRemove,
      onConfirmCancel,
      handleOnInputChange,
      handleSortChange,
      onPolicyChange
    ]
  )
}
